import React, {useState} from 'react';
import intl from "react-intl-universal";

import {useAppState} from "@stores/AppStateContext";

import type {ItemModelType, WorkspaceModelType} from '@stores/WorkspaceStore';
import {ContextMenuInterface} from '../ContextMenuInterface';

import {ContextMenuWithNavigation, ContextMenuWithNavigationItem} from '../../../components/ContextMenuWithNavigation';
import {CheckIcon} from '../../../components/icons/CheckIcon';
import {InputWithClear} from "../../../components/InputWithClear";
import {AddIcon} from "../../../components/icons/AddIcon";
import {DeleteIcon} from "../../../components/icons/DeleteIcon";
import {Tooltip} from "../../../components/Tooltip";
import {MODALS} from '../../../screens/modals/modal-names';
import {PAGES} from "@stores/ViewStore";

import './SectionSelectOptions.css';

export interface SectionSelectOptionsProps extends ContextMenuInterface {
    sections: ItemModelType[];
    selectedSection?: ItemModelType | null;
    workspace: WorkspaceModelType;
    maxHeight?: number;
    maxWidth?: number;
}

export const SectionSelectOptions = (
    {
        handleClose,
        isDark,
        sections,
        selectedSection,
        workspace,
        maxHeight,
        maxWidth,
    }: SectionSelectOptionsProps
) => {
    const appState = useAppState();
    const [filterText, setFilterText] = useState('');
    const filter = filterText.toLowerCase();
    const filteredSections = sections.filter(section =>
        (section.name || '').toLowerCase().includes(filter)
    );

    const openCreateSection = () => {
        appState.setContextMenu(null);
        appState.setModal({name: MODALS.LIBRARY_EDIT, params: {workspace}});
    };

    const openDeleteSection = (e: React.MouseEvent, section: ItemModelType) => {
        e.stopPropagation();
        appState.setContextMenu(null);
        appState.setModal({name: MODALS.SECTION_DELETE, params: {section, workspace}});
    };

    const mapSection = (section: ItemModelType): ContextMenuWithNavigationItem => ({
        id: section.id,
        getContent: (_ctx, meta, innerRef) => (
            <li
                ref={innerRef}
                key={section.id}
                onClick={meta.onClick}
                className="SectionSelectOptions__row"
            >
                <div className="SectionSelectOptions">
                    <div className="SectionSelectOptions__text">
                        {section.name || intl.get('common.untitled_section').d('Untitled Library')}
                    </div>
                    {selectedSection?.id === section.id && (
                        <div className="SectionSelectOptions__mark">
                            <CheckIcon size={16} additionalPadding={2}/>
                        </div>
                    )}
                    <Tooltip title={intl.get('section.delete.tooltip').d('Delete library')}>
                        <DeleteIcon
                            className="SectionSelectOptions__row__delete"
                            size={16}
                            additionalPadding={2}
                            onClick={(e) => openDeleteSection(e, section)}
                        />
                    </Tooltip>
                </div>
            </li>
        ),
        text: section.name || intl.get('common.untitled_section').d('Untitled Library'),
        textForFilter: section.name || '',
        meta: section,
    });

    const itemGroups: ContextMenuWithNavigationItem[][] = [];

    if (filteredSections.length > 0) {
        itemGroups.push(filteredSections.map(mapSection));
    }

    const header = (
        <>
            <InputWithClear
                maxWidth={228}
                withBackground={true}
                value={filterText}
                onChange={setFilterText}
                placeholder={intl.get('section.select.search.placeholder').d("Search Library")}
            />
            <div className="SectionSelectOptions__header label-small-bold">
                {intl.get('SectionSelectOptions.sections').d("LIBRARIES")}
                <Tooltip title={intl.get("SectionSelectOptions.AddSection").d("Create Library")}>
                    <AddIcon
                        className={"SectionSelectOptions__header__icon"}
                        size={20}
                        onClick={openCreateSection}
                    />
                </Tooltip>
            </div>
        </>
    );

    return (
        <ContextMenuWithNavigation
            headerNode={header}
            allowFilter={false}
            maxHeight={maxHeight ? `${maxHeight}px` : undefined}
            maxWidth={maxWidth ? `${maxWidth}px` : undefined}
            className="Select__options"
            handleClose={handleClose}
            withBorder={false}
            itemGroups={itemGroups}
            isDark={isDark}
            onItemClick={(item) => {
                const section = item.meta as ItemModelType;
                workspace.setSelectedLibraryId(section.id);
                appState.rootStore.view.openPageByName(PAGES.KNOWLEDGE_BASE, {
                    libraryId: section.id,
                    librarySlug: section.slug || undefined,
                });
                appState.setContextMenu(null);
            }}
        />
    );
};
